import { isTauri } from '../utils/tauri';
import { quoteIdentFor } from '../extensions/dbEngines';
import { isBytesTag, isUndecoded } from './dbCellTags';

/**
 * Pure serialization helpers + the file-save mechanism for DB Manager grid
 * exports. Kept free of React so the SQL/CSV generators can be exercised in
 * a plain node harness.
 *
 * Save path mirrors utils/exportSave.js / components/documentExport.js:
 * Tauri plugin-dialog `save` + plugin-fs `writeTextFile` (the
 * `fs:allow-write-text-file` capability already covers $HOME/$DOWNLOAD/…),
 * with a Blob+<a download> fallback for non-Tauri contexts.
 */

// Base64 (how binary cells cross the bridge) to the hex digits SQL binary
// literals are written in. Returns '' if the payload isn't decodable, which
// callers treat as "no bytes".
function base64ToHex(b64) {
  try {
    const bin = atob(b64);
    let out = '';
    for (let i = 0; i < bin.length; i += 1) {
      out += bin.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return out;
  } catch {
    return '';
  }
}

// One CSV cell. Quotes anything containing commas/quotes/newlines; objects
// (JSON columns) are stringified. A binary cell exports as its base64, which
// is lossless and the only text form it has; a value the driver could not
// decode exports empty, because we never had it to export.
function csvEscape(v) {
  if (v === null || v === undefined) return '';
  if (isBytesTag(v)) return v.b64;
  if (isUndecoded(v)) return '';
  const str = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
}

// Chunk-friendly halves of the CSV serializer (streamed exports write the
// header once, then append each chunk's lines). Both end with '\n' so
// consecutive chunks never glue together.
export function csvHeader(columns) {
  return columns.map(csvEscape).join(',') + '\n';
}

export function rowsToCsvLines(rows) {
  return rows.map((row) => row.map(csvEscape).join(',') + '\n').join('');
}

// CSV of a whole { columns, rows } result set (clipboard-copy + buffered
// exports; no trailing newline, matching the old clipboard behavior).
export function resultToCsv(result) {
  return [
    result.columns.map(csvEscape).join(','),
    ...result.rows.map((row) => row.map(csvEscape).join(',')),
  ].join('\n');
}

export function resultToJson(result) {
  return JSON.stringify(
    result.rows.map((row) => Object.fromEntries(result.columns.map((c, i) => [c, row[i]]))),
    null,
    2
  );
}

/** Cell text shared by the table serializers; NULL renders empty. */
function cellText(v) {
  if (v === null || v === undefined) return '';
  if (isBytesTag(v)) return v.b64;
  if (isUndecoded(v)) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

/**
 * GitHub-flavoured pipe table — what developers paste into a PR, an issue or a
 * design doc, which is the whole reason this format is here.
 *
 * `|` and `\` are escaped because they would otherwise break the row into the
 * wrong number of cells, and newlines become <br> (GFM's spelling — a literal
 * newline ends the row).
 */
export function resultToMarkdown(result) {
  const esc = (v) =>
    cellText(v)
      .replace(/\\/g, '\\\\')
      .replace(/\|/g, '\\|')
      .replace(/\r?\n/g, '<br>');
  const header = `| ${result.columns.map(esc).join(' | ')} |`;
  const rule = `| ${result.columns.map(() => '---').join(' | ')} |`;
  const body = result.rows.map((row) => `| ${row.map(esc).join(' | ')} |`);
  return [header, rule, ...body].join('\n');
}

/**
 * HTML table, entity-escaped. Emitted as plain text on the clipboard: pasting
 * as rich HTML would need a ClipboardItem, and the markup itself is what you
 * want when pasting into a template or a wiki.
 */
export function resultToHtmlTable(result) {
  const esc = (v) =>
    cellText(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  const head = `    <tr>${result.columns.map((c) => `<th>${esc(c)}</th>`).join('')}</tr>`;
  const body = result.rows.map((row) => `    <tr>${row.map((v) => `<td>${esc(v)}</td>`).join('')}</tr>`);
  return ['<table>', '  <thead>', head, '  </thead>', '  <tbody>', ...body, '  </tbody>', '</table>'].join('\n');
}

/**
 * One SQL value literal. Values arrive as JSON from the Rust bridge, so the
 * cases are: null, number, boolean, string, and object/array (JSON columns).
 * Strings escape ' by doubling; MySQL additionally escapes backslashes
 * (its default sql_mode treats \ as an escape character in string literals —
 * Postgres/SQLite standard-conforming strings do not).
 */
export function sqlLiteral(engine, v) {
  if (v === null || v === undefined) return 'NULL';
  // Binary round-trips as an engine-native hex literal rather than as the text
  // of its base64, which would insert the encoding instead of the bytes.
  if (isBytesTag(v)) {
    const hex = base64ToHex(v.b64);
    // An empty blob has no hex digits, and `0x`/`X''` with none is either
    // invalid or ambiguous — an empty string literal is the portable spelling.
    if (!hex) return "''";
    if (engine === 'postgres') return `'\\x${hex}'::bytea`;
    if (engine === 'sqlite') return `X'${hex}'`;
    return `0x${hex}`;
  }
  // Never decoded, so there is nothing truthful to emit. NULL is the honest
  // answer; an INSERT dump containing it should not claim otherwise.
  if (isUndecoded(v)) return 'NULL';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : `'${String(v)}'`;
  if (typeof v === 'boolean') {
    return engine === 'postgres' ? (v ? 'TRUE' : 'FALSE') : v ? '1' : '0';
  }
  let str = typeof v === 'object' ? JSON.stringify(v) : String(v);
  if (engine === 'mysql') str = str.replace(/\\/g, '\\\\');
  return `'${str.replace(/'/g, "''")}'`;
}

/**
 * Batched multi-row INSERT dump: INSERT INTO <table> (cols) VALUES (…),(…);
 * with `rowsPerStatement` rows per statement (HeidiSQL-style). Identifier
 * quoting is engine-appropriate via quoteIdentFor.
 */
export function resultToInsertSql(engine, tableName, columns, rows, { rowsPerStatement = 100 } = {}) {
  if (!columns || columns.length === 0) return '';
  const table = quoteIdentFor(engine, tableName || 'exported_table');
  const colList = columns.map((c) => quoteIdentFor(engine, c)).join(', ');
  const statements = [];
  for (let i = 0; i < rows.length; i += rowsPerStatement) {
    const chunk = rows.slice(i, i + rowsPerStatement);
    const values = chunk
      .map((row) => `(${row.map((v) => sqlLiteral(engine, v)).join(', ')})`)
      .join(',\n');
    statements.push(`INSERT INTO ${table} (${colList}) VALUES\n${values};`);
  }
  return statements.join('\n\n') + (statements.length ? '\n' : '');
}

function downloadTextBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime || 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Ask for a destination path up front (so long "entire table" exports don't
 * fetch thousands of rows only to be cancelled at the dialog). Returns the
 * chosen path, or null when cancelled, or undefined outside Tauri (meaning:
 * no dialog available — write via saveTextToPath's blob fallback at the end).
 */
export async function pickSavePath({ filename, filters }) {
  if (!isTauri()) return undefined;
  const { save } = await import('@tauri-apps/plugin-dialog');
  const path = await save({
    defaultPath: filename,
    title: 'Export result set',
    filters,
  });
  return path == null ? null : path;
}

/**
 * Write `content` to the path from pickSavePath; a non-Tauri context
 * (path === undefined) falls back to a browser download of `filename`.
 */
export async function saveTextToPath(path, { content, filename, mime }) {
  if (path === undefined) {
    downloadTextBlob(content, filename, mime);
    return { saved: true, path: null };
  }
  const { writeTextFile } = await import('@tauri-apps/plugin-fs');
  await writeTextFile(path, content);
  return { saved: true, path };
}

/**
 * Append to a file created by saveTextToPath — streamed whole-table exports
 * write chunk-by-chunk instead of building one giant string in the webview.
 * Tauri only (the browser fallback has no append; callers buffer there).
 * Covered by the same `fs:allow-write-text-file` capability.
 */
export async function appendTextToPath(path, content) {
  const { writeTextFile } = await import('@tauri-apps/plugin-fs');
  await writeTextFile(path, content, { append: true });
}
